import {
  EV_COMMAND_STATUS_LABELS,
  EV_CONNECTOR_STATUS_LABELS,
  EV_PAYMENT_STATUS_LABELS,
  EV_SESSION_PHASE_LABELS,
  type EvChargerSummary,
  type EvCommandStatus,
  type EvConnectorStatus,
  type EvInvoiceStatus,
  type EvOperationModel,
  type EvPaymentStatus,
  type EvSessionPhaseValue,
  rooms,
} from "@station/contracts";
import {
  Alert,
  type BreadcrumbItem,
  EmptyState,
  FormField,
  PageHeader,
  Select,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { MapPinOff, PlugZap, TriangleAlert } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { errorMessage } from "../../lib/errors.js";
import { useDocumentTitle } from "../../lib/hooks.js";
import { useRoom } from "../../lib/realtime-context.js";
import { useScope } from "../../lib/scope.js";
import { useTenantShape } from "../../lib/tenant-shape.js";
import { useStationScope } from "../cashflow/shared.js";
import {
  COMMAND_STATUS_TONE,
  CONNECTOR_STATUS_TONE,
  INVOICE_STATUS_TONE,
  isPaymentWarning,
  PAYMENT_STATUS_TONE,
  SESSION_PHASE_TONE,
  sessionWarningText,
  TARIFF_STATE_TONE,
  type TariffState,
} from "./ev-values.js";
import { type EvPermissions, useEvPermissions, useEvPickers } from "./queries.js";

export { TableFrame, Th } from "../wash/shared.js";

/** Şarj ekranlarının ortak arama parametresi: istasyon (`?station=`). */
export const evSearchShape = {
  station: z.string().uuid().optional().catch(undefined),
};

export interface EvStationOption {
  id: string;
  code: string;
  name: string;
  companyId: string;
  operationModel: EvOperationModel;
}

export interface EvStationSelection {
  stations: EvStationOption[];
  station: EvStationOption | null;
  /** Tek istasyonlu kiracıda ve görünür tek şarj istasyonunda seçici çizilmez (AGENTS.md §2). */
  showPicker: boolean;
  isLoading: boolean;
  error: unknown;
  select: (stationId: string) => void;
}

/**
 * Şarj İSTASYON bazındadır: yalnızca bayrağı açık, şarj iş birimi etkin ve kullanıcının kapsamındaki istasyonlar seçilebilir
 * (sunucunun seçici listesi — `GET /ev/pickers` bunları süzer). Seçim URL'de taşınır (`?station=`); yoksa üst çubuktaki
 * kapsam (tek istasyon seçiliyse) ya da ilk istasyon kullanılır.
 */
export function useEvStation(enabled = true): EvStationSelection {
  const pickers = useEvPickers(enabled);
  const scope = useStationScope();
  const topbarScope = useScope();
  const shape = useTenantShape();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { station?: string };
  const visible: EvStationOption[] = (pickers.data?.stations ?? []).filter(
    (station) => !scope.restricted || scope.allowedStationIds.includes(station.id),
  );
  const fromScope =
    topbarScope.kind === "station"
      ? visible.find((station) => station.id === topbarScope.stationId)
      : undefined;
  const selected =
    visible.find((station) => station.id === search.station) ?? fromScope ?? visible[0] ?? null;
  return {
    stations: visible,
    station: selected,
    showPicker: visible.length > 1 && !shape.isSingleStation,
    isLoading: enabled && pickers.isPending,
    error: pickers.error,
    select: (stationId) =>
      void navigate({
        to: ".",
        search: ((previous: Record<string, unknown>) => ({ ...previous, station: stationId })) as never,
        replace: true,
      }),
  };
}

export function EvStationField({
  selection,
  className = "w-64",
}: {
  selection: EvStationSelection;
  className?: string;
}): ReactElement | null {
  const { t } = useTranslation();
  if (!selection.showPicker) return null;
  return (
    <FormField label={t("ev.fields.station")} className={className}>
      {(control) => (
        <Select
          {...control}
          value={selection.station?.id ?? ""}
          onChange={(event) => selection.select(event.target.value)}
        >
          {selection.stations.map((station) => (
            <option key={station.id} value={station.id}>
              {station.code} {station.name}
            </option>
          ))}
        </Select>
      )}
    </FormField>
  );
}

export interface EvStationContext {
  station: EvStationOption;
  selection: EvStationSelection;
  permissions: EvPermissions;
}

export type EvRequirement = "read" | "manage" | "operate" | "reports" | "exports";

function hasRequirement(permissions: EvPermissions, requirement: EvRequirement): boolean {
  if (requirement === "manage") return permissions.canRead && permissions.canManage;
  if (requirement === "operate") return permissions.canRead && permissions.canOperate;
  if (requirement === "reports") return permissions.canReadReports;
  if (requirement === "exports") return permissions.canSeeExports;
  return permissions.canRead;
}

/** Bayrak ve izin kapısı (sayfa çerçeveleri ortak kullanır). */
function EvGate({
  requirement,
  children,
}: {
  requirement: EvRequirement;
  children: ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useEvPermissions();
  if (!permissions.enabled)
    return (
      <EmptyState
        headingLevel={2}
        icon={<PlugZap />}
        title={t("ev.disabledTitle")}
        description={t("ev.disabledDescription")}
      />
    );
  if (!hasRequirement(permissions, requirement))
    return <Alert tone="info">{t(`ev.noPermission.${requirement}`)}</Alert>;
  // Izgara öğeleri içerikleri kadar genişlemesin (grafik tuvali, geniş tablo): tablet genişliğinde sayfa yatay kaymaz.
  return <div className="grid min-w-0 gap-4 [&>*]:min-w-0">{children}</div>;
}

/** KİRACI / ŞİRKET düzeyindeki şarj ekranları (tarifeler, dışa aktarım, ayrıntılar): başlık + kırıntı + kapı. */
export function EvPage({
  title,
  description,
  breadcrumb,
  actions,
  requirement = "read",
  children,
}: {
  title: string;
  description?: string | undefined;
  breadcrumb?: BreadcrumbItem[];
  actions?: ReactNode;
  requirement?: EvRequirement;
  children: ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useEvPermissions();
  useDocumentTitle(title);
  const open = permissions.enabled && hasRequirement(permissions, requirement);
  return (
    <>
      <PageHeader
        title={title}
        {...(description ? { description } : {})}
        breadcrumb={[{ label: t("menu.groups.ev") }, ...(breadcrumb ?? [{ label: title }])]}
        actions={open ? actions : null}
      />
      <EvGate requirement={requirement}>{children}</EvGate>
    </>
  );
}

/**
 * İSTASYON düzeyindeki şarj ekranları: başlıkta istasyon seçici; istasyon odasına abone olunur (konnektör durumu, oturum,
 * komut olayları önbelleği tazeler). İstasyon değişince sayfanın yerel durumu sıfırlanır.
 */
export function EvStationPage({
  title,
  description,
  breadcrumb,
  actions,
  requirement = "read",
  children,
}: {
  title: string;
  description?: string | undefined;
  breadcrumb?: BreadcrumbItem[];
  actions?: (context: EvStationContext) => ReactNode;
  requirement?: EvRequirement;
  children: (context: EvStationContext) => ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useEvPermissions();
  useDocumentTitle(title);
  const open = permissions.enabled && hasRequirement(permissions, requirement);
  const selection = useEvStation(open);
  const station = selection.station;
  useRoom(open && station ? rooms.station(station.id) : null);
  const context = station ? { station, selection, permissions } : null;
  return (
    <>
      <PageHeader
        title={title}
        {...(description ? { description } : {})}
        breadcrumb={[{ label: t("menu.groups.ev") }, ...(breadcrumb ?? [{ label: title }])]}
        actions={
          open ? (
            // Seçici (etiket + kutu) ile düğmeler ALT çizgide hizalanır.
            <div className="flex flex-wrap items-end gap-2">
              <EvStationField selection={selection} />
              {context && actions ? actions(context) : null}
            </div>
          ) : null
        }
      />
      <EvGate requirement={requirement}>
        {selection.error ? (
          <Alert tone="danger">{errorMessage(selection.error)}</Alert>
        ) : selection.isLoading ? (
          <SkeletonRows rows={6} />
        ) : context === null ? (
          <EmptyState
            headingLevel={2}
            icon={<MapPinOff />}
            title={t("ev.noStationsTitle")}
            description={t("ev.noStationsDescription")}
            action={
              <Link to="/ayarlar/istasyonlar" className="text-sm underline underline-offset-4">
                {t("menu.stations")}
              </Link>
            }
          />
        ) : (
          <div key={context.station.id} className="grid min-w-0 gap-4 [&>*]:min-w-0">
            {children(context)}
          </div>
        )}
      </EvGate>
    </>
  );
}

/** Kırıntıda bir şarj listesine geri bağlantı (ayrıntı sayfaları). */
export function evCrumb(
  label: string,
  to: "/sarj/uniteler" | "/sarj/oturumlar",
  search?: Record<string, string>,
): BreadcrumbItem {
  return {
    label,
    link: (children, className) => (
      <Link to={to} search={(search ?? {}) as never} className={className}>
        {children}
      </Link>
    ),
  };
}

// ── Rozetler (anlam yalnızca renkle taşınmaz: rozet METİN yazar) ─────────────
export function ConnectorStatusBadge({
  status,
  errorCode,
}: {
  status: EvConnectorStatus | null;
  errorCode?: string | null;
}): ReactElement {
  const { t } = useTranslation();
  if (status === null)
    return (
      <StatusBadge tone="neutral" className="whitespace-nowrap">
        {t("ev.connector.noStatus")}
      </StatusBadge>
    );
  return (
    <StatusBadge tone={CONNECTOR_STATUS_TONE[status]} className="whitespace-nowrap">
      {EV_CONNECTOR_STATUS_LABELS[status]}
      {status === "Faulted" && errorCode && errorCode !== "NoError" ? ` · ${errorCode}` : ""}
    </StatusBadge>
  );
}

export function OnlineBadge({ online }: { online: boolean }): ReactElement {
  const { t } = useTranslation();
  return online ? (
    <StatusBadge tone="success" className="whitespace-nowrap">
      {t("ev.connector.online")}
    </StatusBadge>
  ) : (
    <StatusBadge tone="neutral" className="whitespace-nowrap">
      {t("ev.connector.offline")}
    </StatusBadge>
  );
}

export function SessionPhaseBadge({ phase }: { phase: EvSessionPhaseValue }): ReactElement {
  return (
    <StatusBadge tone={SESSION_PHASE_TONE[phase]} className="whitespace-nowrap">
      {EV_SESSION_PHASE_LABELS[phase]}
    </StatusBadge>
  );
}

export function PaymentStatusBadge({ status }: { status: EvPaymentStatus }): ReactElement {
  return (
    <StatusBadge tone={PAYMENT_STATUS_TONE[status]} className="whitespace-nowrap">
      {EV_PAYMENT_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

export function InvoiceStatusBadge({ status }: { status: EvInvoiceStatus }): ReactElement | null {
  const { t } = useTranslation();
  if (status === "NONE") return null;
  return (
    <StatusBadge tone={INVOICE_STATUS_TONE[status]} className="whitespace-nowrap">
      {t(`ev.invoiceStatus.${status}`)}
    </StatusBadge>
  );
}

export function CommandStatusBadge({ status }: { status: EvCommandStatus }): ReactElement {
  return (
    <StatusBadge tone={COMMAND_STATUS_TONE[status]} className="whitespace-nowrap">
      {EV_COMMAND_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

export function TariffStateBadge({ state }: { state: TariffState }): ReactElement {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={TARIFF_STATE_TONE[state]} className="whitespace-nowrap">
      {t(`ev.tariffs.state.${state}`)}
    </StatusBadge>
  );
}

/** Kimlik bilgisi durumu: şarj ağı / OCPP kimliği yok / parola yok / devre dışı / etkin. */
export function CredentialBadge({
  charger,
}: {
  charger: Pick<EvChargerSummary, "credential" | "operationModel" | "ocppIdentity">;
}): ReactElement {
  const { t } = useTranslation();
  if (charger.operationModel === "NETWORK_PARTNER")
    return <StatusBadge tone="neutral">{t("ev.chargers.credential.partner")}</StatusBadge>;
  if (!charger.ocppIdentity)
    return <StatusBadge tone="warning">{t("ev.chargers.credential.noIdentity")}</StatusBadge>;
  if (!charger.credential.configured)
    return <StatusBadge tone="warning">{t("ev.chargers.credential.none")}</StatusBadge>;
  if (!charger.credential.enabled)
    return <StatusBadge tone="danger">{t("ev.chargers.credential.disabled")}</StatusBadge>;
  return <StatusBadge tone="success">{t("ev.chargers.credential.active")}</StatusBadge>;
}

/**
 * Tanım listesi satırı (`<dl>` içinde). Izgarada yan yana duran alanların yükseklikleri farklı olsa da etiket ve değer
 * üstte kalır (`content-start`: ızgara satırı gerilince iç satırlar yayılmaz).
 */
export function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}): ReactElement {
  return (
    <div className={`grid content-start gap-0.5 ${className ?? ""}`}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

/** Oturum uyarıları Türkçe etiketleriyle; ödeme bağı uyarıları (elle düzeltme) tehlike tonunda. */
export function SessionWarnings({
  warnings,
  compact = false,
}: {
  warnings: readonly string[];
  compact?: boolean;
}): ReactElement | null {
  if (warnings.length === 0) return null;
  return (
    <ul className={`grid gap-1 ${compact ? "text-xs" : "text-sm"}`}>
      {warnings.map((code) => (
        <li
          key={code}
          className={`flex items-start gap-1.5 ${isPaymentWarning(code) ? "text-destructive" : "text-warning"}`}
        >
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <span>{sessionWarningText(code)}</span>
        </li>
      ))}
    </ul>
  );
}
